/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import i18nContext from 'components/i18n-context';
import BottomPartForm from './BottomPartForm';
import { clearJunctionModel } from './Models';
import clearJunctionValidation from './ModelValidators/clearJunctionValidation';
import { ReactComponent as LocalIcon } from 'assets/payments.svg';
import { ReactComponent as IbanIcon } from 'assets/payment-advanced.svg';
import { CURRENCY_TYPES, PAYMENT_METHOD, RECIPIENT_TYPES } from 'components/common/constants';
import { amountFormattedValue, formatSortCode, prepareFieldErrors, strToFloat } from 'services/utils';
import { IconButton } from 'uikit/IconButton/IconButton';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';

const LOCAL_TAB = 'LOCAL_ACCOUNT';
const IBAN_TAB = 'IBAN';

const CleatJunctionTable = ({
  customerNumber,
  calculateCommission,
  checkTransfer,
  uploadDocuments,
  setIban,
  setIsNonIban,
  ibanCredentials,
  clearIbanCredentials,
  account,
  isRepeatPayment,
  checkIban,
  paymentMethod,
  resetPaymentMethods,
  uploadedFiles,
  commission,
  removePaymentFile,
  error,
  setError,
  isCommissionLoading,
  isLoading,
  isSuccess,
  currency,
  validationProps,
  isFormResetAfterAccountChanging,
  setIsFormResetAfterAccountChanging,
  setIsPreparedTransactionsCreation,
  isForApprovalTransactionEdit,
  isUserCanCreatePreparedTransaction,
  isUserCanSendTransaction,
  validateForApprovalTransaction
}) => {
  const i18n = useContext(i18nContext);
  const model = clearJunctionModel;
  const validationSchema = clearJunctionValidation({ ...validationProps, model });
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [disabled, setDisabled] = useState(true);
  const [showMethodCommission, setShowMethodCommission] = useState(false);
  const form = useFormik(validationSchema);
  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    validateField,
    validateForm,
    resetForm,
    setFieldValue,
    setErrors
  } = form;
  const [activeTab, setActiveTab] = useState(
    currency === CURRENCY_TYPES.GBP && !values.recipient_wallet ? LOCAL_TAB : IBAN_TAB
  );

  useEffect(() => {
    if (activeTab === LOCAL_TAB) {
      setIsNonIban(true);
    } else {
      setIsNonIban(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (isRepeatPayment) {
      const asyncOperations = async () => {
        await handleIbanComplete(false);
      };

      asyncOperations();
    } else {
      resetForm();
      setIsFormResetAfterAccountChanging(true);
    }
  }, [account.wallet_number]);

  useEffect(() => {
    if (isSuccess) {
      resetForm({});
      setDisabled(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    const asyncOperations = async () => {
      if (!isFormResetAfterAccountChanging) return;

      if (paymentMethod === PAYMENT_METHOD.INTERNAL && activeTab === LOCAL_TAB) {
        setFieldValue('account_number', '');
        setFieldValue('sort_code', '');
        delete errors['account_number'];
        delete errors['sort_code'];
        setErrors(errors);
        setActiveTab(IBAN_TAB);
      }

      if (
        (isRepeatPayment && !values.recipient_wallet && activeTab === LOCAL_TAB) ||
        (!isRepeatPayment && activeTab === IBAN_TAB)
      ) {
        if (values.amount && paymentMethod) {
          await calculateCommission(customerNumber, {
            walletNumber: account?.wallet_number,
            paymentMethod: paymentMethod,
            amount: strToFloat(values.amount?.toString() || '')
          });
        }

        if (!isForApprovalTransactionEdit) {
          await handleCheckTransfer(undefined);
        }
      }
    };

    asyncOperations();
  }, [paymentMethod, isRepeatPayment, isFormResetAfterAccountChanging, activeTab]);

  const handleCheckTransfer = async (fieldName) => {
    if (fieldName) await validateField(fieldName);
    if (
      !serverFieldsErrors?.iban &&
      (values.recipient_wallet || (values.account_number && values.sort_code)) &&
      values.amount &&
      values.recipient_type &&
      values.comment &&
      (values.recipient_type === RECIPIENT_TYPES.COMPANY ? values.company_name : values.first_name && values.last_name)
    ) {
      const errorsResult = await validateForm();
      if (!Object.keys(errorsResult).length) {
        await checkTransfer(
          customerNumber,
          model({
            providerTableData: values,
            data: { wallet: account, paymentMethod, ...ibanCredentials }
          })
        );
        setShowMethodCommission(true);
        setDisabled(false);
      }
    }
  };

  const handleRecipientTypeChange = (name, data) => handleChange({ target: { name, value: data } });
  const onAmountIBANChange = (e) => {
    setShowMethodCommission(false);
    handleChange(e);
    if (e.target.name === 'recipient_wallet') {
      setIban(e.target.value);
    }
  };

  const getCommissionValue = () => {
    if (!commission.value && !commission.currency) {
      return '0.00';
    }
    return `${commission.commissionDetails?.displayAmount || amountFormattedValue(commission.value)}`;
  };

  const handleActiveTab = (activeTab) => {
    if (ibanCredentials) clearIbanCredentials();
    if (error) setError(null);
    if (!paymentMethod) resetPaymentMethods();
    if (activeTab === LOCAL_TAB) {
      setFieldValue('recipient_wallet', '');
      delete errors['recipient_wallet'];
    }
    if (activeTab === IBAN_TAB) {
      setFieldValue('account_number', '');
      setFieldValue('sort_code', '');
      delete errors['account_number'];
      delete errors['sort_code'];
    }
    setErrors(errors);
    setActiveTab(activeTab);
  };

  const onSortCodeChange = (e) => {
    let inputValue = e.target.value;
    let numericValue = formatSortCode(inputValue);
    setFieldValue('sort_code', numericValue);
  };

  const handleIbanComplete = async (causedOnBlur) => {
    if (causedOnBlur) await validateField('recipient_wallet');
    values.recipient_wallet && (await checkIban(values.recipient_wallet));
    if (causedOnBlur && !isForApprovalTransactionEdit) await handleCheckTransfer(undefined);
  };

  const bottomPartProps = {
    values,
    errors,
    error,
    handleChange,
    handleSubmit,
    serverFieldsErrors,
    onAmountIBANChange,
    calculateCommission,
    handleCheckTransfer,
    getCommissionValue,
    paymentMethod,
    paymentProvider: account.transfer_provider,
    isSubmitDisabled:
      (isForApprovalTransactionEdit ? false : disabled) ||
      isLoading ||
      isCommissionLoading ||
      !!Object.keys(errors).length ||
      !!Object.keys(serverFieldsErrors).length ||
      !!error ||
      !paymentMethod,
    isLoading: isLoading || isCommissionLoading,
    uploadedFiles,
    uploadDocuments,
    removePaymentFile,
    customerNumber,
    currentWalletNumber: account?.wallet_number,
    commission,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction,
    validateForApprovalTransaction,
    model: clearJunctionModel,
    setError
  };

  return (
    <>
      {currency === CURRENCY_TYPES.GBP && paymentMethod !== PAYMENT_METHOD.INTERNAL && (
        <div className={'navigation-payment'}>
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.divider.simpleForm')}
            active={activeTab === LOCAL_TAB}
            isDisabled={paymentMethod === PAYMENT_METHOD.INTERNAL}
            onClick={() => handleActiveTab(LOCAL_TAB)}
            Icon={LocalIcon}
          />
          <IconButton
            className={'navigation-payment-button'}
            text={i18n.getMessage('transfer.label.iban')}
            active={activeTab === IBAN_TAB}
            onClick={() => handleActiveTab(IBAN_TAB)}
            Icon={IbanIcon}
          />
        </div>
      )}
      {(activeTab === IBAN_TAB || account.currency === CURRENCY_TYPES.EUR) && (
        <div className={'inputs-wrapper'}>
          <Input
            isRequired={true}
            isDisabled={!!values.account_number || !!values.sort_code}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.recipientIban.label')}
            name={'recipient_wallet'}
            value={values.recipient_wallet}
            error={
              (!values.account_number && errors.recipient_wallet) ||
              serverFieldsErrors.iban ||
              serverFieldsErrors.recipientIban
            }
            onChange={onAmountIBANChange}
            onBlur={() => handleIbanComplete(true)}
            subText={
              ibanCredentials?.bank_name &&
              i18n.getMessage('transfer.form.bankName.subText', { bankName: ibanCredentials?.bank_name })
            }
          />
        </div>
      )}
      {currency === CURRENCY_TYPES.GBP && activeTab === LOCAL_TAB && (
        <div className={'inputs-wrapper-evenly account-number-and-sort-code-wrapper'}>
          <Input
            isRequired={true}
            isDisabled={!!values.recipient_wallet}
            autoComplete={'off'}
            name={'account_number'}
            label={i18n.getMessage('transfer.form.accountNumberGBP.label')}
            value={values.account_number}
            error={errors.account_number || serverFieldsErrors.account_number}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('account_number')}
          />
          <Input
            isRequired={true}
            isDisabled={!!values.recipient_wallet}
            autoComplete={'off'}
            name={'sort_code'}
            label={i18n.getMessage('transfer.form.sortCode.label')}
            value={values.sort_code}
            error={errors.sort_code || serverFieldsErrors.sort_code}
            onChange={onSortCodeChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('sort_code')}
            max={8}
          />
        </div>
      )}
      <div className={`inputs-wrapper ${values.recipient_type}`}>
        <InputDropDown
          label={i18n.getMessage('transfer.form.recipientType.label')}
          autoComplete={'off'}
          name={'recipient_type'}
          value={values.recipient_type}
          onChange={handleRecipientTypeChange}
          options={Object.values(RECIPIENT_TYPES).map((value) => ({ key: value, value }))}
        />
        {values.recipient_type === RECIPIENT_TYPES.COMPANY && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.companyName.label')}
            name={'company_name'}
            value={values.company_name}
            error={errors.company_name || serverFieldsErrors.companyName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('company_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.firstName.label')}
            name={'first_name'}
            value={values.first_name}
            error={errors.first_name || serverFieldsErrors.firstName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('first_name')}
          />
        )}
        {values.recipient_type === RECIPIENT_TYPES.INDIVIDUAL && (
          <Input
            isRequired={true}
            autoComplete={'off'}
            label={i18n.getMessage('transfer.form.lastName.label')}
            name={'last_name'}
            value={values.last_name}
            error={errors.last_name || serverFieldsErrors.lastName}
            onChange={handleChange}
            onBlur={() => !isForApprovalTransactionEdit && handleCheckTransfer('last_name')}
          />
        )}
      </div>
      <BottomPartForm {...bottomPartProps} />
    </>
  );
};

CleatJunctionTable.propTypes = {
  customerNumber: PropTypes.string,
  calculateCommission: PropTypes.func.isRequired,
  checkTransfer: PropTypes.func.isRequired,
  uploadDocuments: PropTypes.func.isRequired,
  setIban: PropTypes.func,
  setIsNonIban: PropTypes.func,
  ibanCredentials: PropTypes.object,
  clearIbanCredentials: PropTypes.func,
  checkIban: PropTypes.func.isRequired,
  isRepeatPayment: PropTypes.bool,
  account: PropTypes.object,
  paymentMethod: PropTypes.string,
  resetPaymentMethods: PropTypes.func,
  commission: PropTypes.object,
  isCommissionLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  setError: PropTypes.func,
  uploadedFiles: PropTypes.array,
  removePaymentFile: PropTypes.func,
  isSuccess: PropTypes.bool,
  currency: PropTypes.string,
  validationProps: PropTypes.object.isRequired,
  isFormResetAfterAccountChanging: PropTypes.bool,
  setIsFormResetAfterAccountChanging: PropTypes.func,
  setIsPreparedTransactionsCreation: PropTypes.func,
  isForApprovalTransactionEdit: PropTypes.bool,
  isUserCanCreatePreparedTransaction: PropTypes.bool,
  isUserCanSendTransaction: PropTypes.bool,
  validateForApprovalTransaction: PropTypes.func
};

export default CleatJunctionTable;
